import { Box, Button, Card, Typography } from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import arrayMutators from 'final-form-arrays'
import get from 'lodash/get'
import { cloneElement, useState } from 'react'
import {
  ArrayField,
  ArrayInput,
  Datagrid,
  DateField,
  FileField,
  FileInput,
  FormDataConsumer,
  Loading,
  maxLength,
  minLength,
  NumberInput,
  required,
  sanitizeEmptyValues,
  SelectInput,
  ShowButton,
  SimpleFormIterator,
  TextField,
  TextInput,
  Toolbar,
  TopToolbar,
  useEditController,
  useNotify,
  useRedirect,
} from 'react-admin'
import { Field, Form } from 'react-final-form'
import { SOURCE_NAME_CHOICES } from 'shared/helpers/constants'

import { LoadStopReferenceCodeInput } from 'components/Common/Loads'
import { formatTripRate } from 'helpers/jobs'
import {
  captureExceptionIfProduction,
  getBrokerReferenceFilter,
  getLoadStop,
  getReadableErrorMessage,
  localizeStopDateTimesForIntitalValues,
  sanitizeNonNullableFieldsOnLoad,
  stripOffsetsFromStopDateTimes,
} from 'helpers/utils'
import { updateAndApprove, upload } from '../../api/jobs'
import {
  DESTINATION,
  DocumentTypes,
  LoadingTypeChoices,
  ORIGIN,
  STOP_TYPE_CHOICES,
  StopTypeDetailChoices,
  TIMEZONE_CHOICES,
} from '../../helpers/constants'
import { getCountryFromCode } from '../../helpers/countries'
import { getStateChoices } from '../../helpers/states'
import {
  isEligibleForVirtualCarrier,
  validateDollarAmount,
  validateLoadStops,
  validatePositiveInteger,
} from '../../helpers/validators'
import { BrokerReferenceInput, BrokerReferenceInputClassName } from '../Common/Brokers'
import { BookApproveLoadButton, BookingType } from '../Common/FeasibilityCheck'
import { PDFPreviewField, StopDateTimeInputWindow, StopSequenceNumberWrapper } from '../Fields'

const validateRequired = [required()]
const validateConfirmedRate = [required(), validateDollarAmount]

const ApproveJobActions = ({ basePath, data }: any) => {
  return (
    <TopToolbar>
      <ShowButton basePath={basePath} record={data} />
    </TopToolbar>
  )
}

const EditWrapper = (props: any) => {
  const {
    basePath, // deduced from the location, useful for action buttons
    loaded, // boolean that is false until the record is available
    record, // record fetched via dataProvider.getOne() based on the id from the location
    redirect, // the default redirection route. Defaults to 'list'
    resource, // the resource name, deduced from the location. e.g. 'posts'
    save, // the update callback, to be passed to the underlying form as submit handler
    saving, // boolean that becomes true when the dataProvider is called to update the record
    version, // integer used by the refresh feature
  } = useEditController(props)
  return loaded ? (
    <div>
      <h1>Approve Job</h1>
      <ApproveJobActions basePath={basePath} data={record} />
      <Card>
        {cloneElement(props.children, {
          basePath,
          record,
          redirect,
          resource,
          save,
          saving,
          version,
        })}
      </Card>
    </div>
  ) : null
}

const ApproveJobForm = ({ record, version }: any) => {
  const redirectTo = useRedirect()
  const notify = useNotify()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  // original record values when form was created
  const [originalRecord] = useState(record)
  // previous record submitted if there was an error in the form submission
  const [previousRecord, setPreviousRecord] = useState(null)
  if (previousRecord !== null) {
    record = previousRecord
  }

  if (!record) {
    return null
  }
  // We need to strip the timezone offset for stop time inputs (if edited) in order to send unaware datetimes to our endpoint
  const submit = (values: any) => {
    setLoading(true)
    values = sanitizeEmptyValues(record, values)
    values = sanitizeNonNullableFieldsOnLoad(values, true)
    values = stripOffsetsFromStopDateTimes(values)
    let originStop = getLoadStop(values.load, ORIGIN)
    let destStop = getLoadStop(values.load, DESTINATION)
    values.assigned_to = values.assigned_to?.id || values.assigned_to
    values.load.origin_pickup_date = originStop.date_start
    values.load.dest_delivery_date = destStop.date_start
    // for externally-booked loads, the source_id will need to be set in addition to source_human_id
    if (!values.load.source_id) {
      values.load.source_id = values.load.source_human_id
    }

    let uploadOrEmpty = new Promise(function (resolve) {
      resolve('')
    })
    const formData = new FormData()
    if (values.rate_confirmation) {
      for (var attachment of values.rate_confirmation) {
        formData.append('attachments', attachment.rawFile)
      }
      formData.append('type', DocumentTypes.RateConfirmation)
      uploadOrEmpty = upload(record.id, formData)
    }

    uploadOrEmpty
      .then(() => {
        return updateAndApprove(record.id, values)
      })
      .then(() => {
        redirectTo(`/jobs/${record.id}/show`)
        notify('Job approved!', { type: 'success' })
      })
      .catch((error) => {
        setPreviousRecord({ ...values })

        const messageAlreadyApproved = get(error, 'response.data.status[0]')
        const messageDatesInvalid = get(error, 'response.data.load.stops[0].date_start[0]')
        const status = get(error, 'response.status')
        // if backend returns a validation error that suggests the job is no longer PENDING, then job has already
        // been approved by another user. no need to capture exception, just show user explanation and redirect to job page.
        if (status === 400 && messageAlreadyApproved?.includes("This field must be 'PENDING'")) {
          notify('Looks like someone already approved this job while you were working on it.', {
            type: 'info',
          })
          redirectTo(`/jobs/${record.id}/show`)
        } else if (
          status === 400 &&
          messageDatesInvalid?.includes("must be less than following stop's 'date_end'")
        ) {
          notify(`Looks like the stop dates are out of order: ${getReadableErrorMessage(error)}`, {
            type: 'warning',
          })
        } else {
          // otherwise, something else went wrong, so capture exception and show user error message
          notify(`Oops, we ran into a problem processing the request: ${getReadableErrorMessage(error)}`, {
            type: 'warning',
          })
          // only raise sentry exception if error response is meaningful
          if (error?.response) {
            captureExceptionIfProduction(error)
          }
        }
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const validateRequiredIfNoRateConfirmation = (value: any) => {
    const recordHasRateCon =
      record.documents &&
      record.documents.filter((document: any) => document.type === DocumentTypes.RateConfirmation).length > 0
    const rateConIsUploaded = value && value.length > 0
    if (recordHasRateCon || rateConIsUploaded) {
      return undefined
    }
    return 'Rate Confirmation is required.'
  }

  const formValidate = (values: any) => {
    const load = values.load
    const is_premium = get(values, 'team.is_premium')
    const load_stop_errors = validateLoadStops(load)
    let broker_errors = {}
    if (is_premium && load.broker) {
      broker_errors = isEligibleForVirtualCarrier(load.broker, is_premium)
    }
    const errors = Object.assign({}, load_stop_errors, broker_errors)
    if (load_stop_errors.load_stops) {
      setError(load_stop_errors.load_stops)
    } else {
      setError(null)
    }
    return errors
  }

  var initialValues = { ...record }
  initialValues = localizeStopDateTimesForIntitalValues(initialValues)

  const brokerInputFieldFilters = getBrokerReferenceFilter(record?.team?.is_premium)

  const country: any = getCountryFromCode('US')
  const stateChoices = getStateChoices(country.states(true))
  // if it's the first time opening the form, make the field blank to enforce it's typed out
  if (previousRecord === null) {
    initialValues.confirmed_rate = null
  }

  return loading ? (
    <Loading />
  ) : (
    <Form
      initialValues={initialValues}
      onSubmit={submit}
      mutators={{ ...arrayMutators }}
      subscription={defaultSubscription}
      key={version}
      keepDirtyOnReinitialize
      validate={formValidate}
      render={(formProps) => {
        return (
          <form onSubmit={formProps.handleSubmit}>
            <Box p="1em">
              {error ? (
                <Box display="flex">
                  <Alert severity="error">{error}</Alert>
                </Box>
              ) : null}
              <Box display="flex">
                <Box flex={1} mr="1em">
                  <Typography variant="h6" gutterBottom>
                    Upload Rate Con
                  </Typography>
                  <Box display="flex">
                    <Box flex={1} mr="0.5em">
                      <FileInput
                        source="rate_confirmation"
                        label="Rate Confirmation"
                        multiple={true}
                        accept="application/pdf,image/jpeg,image/png,image/gif"
                        maxSize={30000000}
                        validate={validateRequiredIfNoRateConfirmation}
                      >
                        <PDFPreviewField source="src" />
                      </FileInput>
                    </Box>
                  </Box>
                  <Box flex={1} maxWidth="140px">
                    <Typography variant="h6" gutterBottom>
                      Uploaded Documents
                    </Typography>
                    <ArrayField record={record} source="documents">
                      <Datagrid>
                        <TextField source="type" />
                        <ArrayField source="attachments">
                          <Datagrid>
                            <FileField source="file" title="original_filename" label="" />
                          </Datagrid>
                        </ArrayField>
                        <DateField source="date_created" options={{ timeZoneName: 'short' }} />
                        <DateField source="date_updated" options={{ timeZoneName: 'short' }} />
                      </Datagrid>
                    </ArrayField>
                  </Box>
                </Box>
                <Box flex={1} ml="1em">
                  <Typography variant="h6" gutterBottom>
                    Confirm Stop Information
                  </Typography>
                  <Box flex={2} mr="1em">
                    <ArrayInput source="load.stops" label="Stops">
                      <SimpleFormIterator disableRemove>
                        <StopSequenceNumberWrapper label="Sequence number" validate={validateRequired} />
                        <SelectInput
                          source="type"
                          label="Stop Type"
                          choices={STOP_TYPE_CHOICES}
                          validate={validateRequired}
                        />
                        <SelectInput
                          source="type_detail"
                          label="Stop Type Detail"
                          choices={StopTypeDetailChoices}
                          validate={validateRequired}
                        />
                        <SelectInput
                          source="loading_type"
                          label="Loading type"
                          choices={LoadingTypeChoices}
                        />
                        <Typography component="div">
                          <Box fontWeight="fontWeightBold">
                            Note: Datetime inputs are local to the timezone field below
                          </Box>
                        </Typography>
                        <StopDateTimeInputWindow />
                        <SelectInput
                          source="location_timezone"
                          label="Timezone"
                          choices={TIMEZONE_CHOICES}
                          validate={validateRequired}
                        />
                        <TextInput source="location_name" label="Location name" />
                        <TextInput source="location_address1" label="Address 1" validate={validateRequired} />
                        <TextInput source="location_address2" label="Address 2" />
                        <TextInput source="location_city" label="City" validate={validateRequired} />
                        <SelectInput
                          source="location_state"
                          label="State"
                          choices={stateChoices}
                          validate={[required(), maxLength(2), minLength(2)]}
                        />
                        <TextInput source="location_zip" label="Zip" validate={validateRequired} />
                        <NumberInput
                          source="truck_weight_lb"
                          label="Truck weight (lbs)"
                          validate={validatePositiveInteger}
                        />
                        {/* Require only the first load stop's reference_code */}
                        <FormDataConsumer>
                          {({ getSource }) =>
                            getSource ? (
                              <LoadStopReferenceCodeInput
                                referenceCodeSource={getSource('reference_code')}
                                isRequired={getSource('reference_code') === 'load.stops[0].reference_code'}
                              />
                            ) : null
                          }
                        </FormDataConsumer>
                        <TextInput source="notes" label="Notes" multiline fullWidth />
                      </SimpleFormIterator>
                    </ArrayInput>
                  </Box>
                </Box>
              </Box>
              <Box display="flex">
                <Box flex={1} ml="1em">
                  <Typography variant="h6" gutterBottom>
                    Confirm Job Information
                  </Typography>
                  <Box display="flex">
                    <Box flex={1} mr="0.5em">
                      <SelectInput
                        source="load.source"
                        label="Source"
                        validate={validateRequired}
                        choices={SOURCE_NAME_CHOICES}
                      />
                    </Box>
                    <Box flex={1}>
                      <TextInput
                        source="load.source_human_id"
                        label="Source ID"
                        validate={validateRequired}
                      />
                    </Box>
                    <Box flex={1}>
                      <NumberInput
                        source="confirmed_rate"
                        label="Confirmed rate"
                        validate={validateConfirmedRate}
                        helperText={
                          originalRecord.confirmed_rate
                            ? `User entered: ${formatTripRate(originalRecord.confirmed_rate)}`
                            : ''
                        }
                      />
                    </Box>
                    <Box flex={1}>
                      <NumberInput disabled source="load.trip_rate" label="Trip rate" />
                    </Box>
                    <Box flex={1}>
                      <NumberInput source="load.trip_distance_mi" label="Trip distance (miles)" />
                    </Box>
                  </Box>
                  <Box display="flex">
                    <Box flex={1}>
                      <TextInput source="load.company_name" label="Name of shipper" />
                    </Box>
                    <Box flex={1} mr="0.5em">
                      <TextInput source="load.contact_name" label="Load contact name" />
                    </Box>
                    <Box flex={1} mr="0.5em">
                      <TextInput source="load.contact_email" label="Load contact email" />
                    </Box>
                    <Box flex={1}>
                      <TextInput source="load.contact_phone" label="Load contact phone" />
                    </Box>
                  </Box>
                  <Box display="flex">
                    <Field name="load.broker">
                      {({ meta }) => (
                        <Box flex={1}>
                          {record.load.broker ? (
                            <TextInput source="load.broker.name" label="Broker" disabled />
                          ) : (
                            <BrokerReferenceInput
                              source="load.broker_id"
                              label="Broker"
                              reference="brokers"
                              filter={brokerInputFieldFilters}
                              filterToQuery={(searchText: any) => ({ search: searchText })}
                              containerClassName={BrokerReferenceInputClassName.jobApprove}
                              validate={validateRequired}
                            />
                          )}
                          {meta.error && meta.error !== 'ra.validation.required' && (
                            <Alert severity="error">{meta.error}</Alert>
                          )}
                        </Box>
                      )}
                    </Field>
                  </Box>
                  <Box display="flex">
                    <Box flex={1}>
                      <TextInput source="load.comment" label="Broker notes" multiline fullWidth />
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
            <Toolbar>
              <Box display="flex" justifyContent="space-between" width="100%">
                {error ? (
                  <Alert severity="error">{error}</Alert>
                ) : record.booking_type === BookingType.standard ? (
                  <BookApproveLoadButton
                    id={record.id}
                    load={record.load}
                    assignedTo={record.assigned_to.id}
                    bookingType={BookingType.standard}
                    onSubmit={formProps.handleSubmit}
                  />
                ) : (
                  <Button name="confirm" variant="contained" color="primary" type="submit">
                    Approve Job
                  </Button>
                )}
              </Box>
            </Toolbar>
          </form>
        )
      }}
    />
  )
}

const defaultSubscription = {
  submitting: true,
  pristine: true,
  valid: true,
  invalid: true,
}

export default function ApproveJob(props: any) {
  return (
    <EditWrapper basePath="/jobs" resource="jobs" id={props.match.params.id} {...props}>
      <ApproveJobForm />
    </EditWrapper>
  )
}
